import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft, Save, Plus, Trash2, Mail, CheckSquare, ArrowRightCircle,
  Clock, Zap, Loader2, GripVertical, ShieldCheck, MailPlus,
  ChevronDown, ChevronUp, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SERVICE_SCOPES = [
  { value: "all", label: "All Services" },
  { value: "roofing", label: "Roofing" },
  { value: "fencing", label: "Fencing" },
  { value: "landscaping", label: "Landscaping" },
  { value: "water", label: "Water Systems" },
];

const EVENT_TRIGGERS = [
  { value: "quote_sent", label: "Quote Sent", desc: "When a quote is emailed to a customer" },
  { value: "quote_signed", label: "Quote Signed", desc: "When a customer signs a quote" },
  { value: "payment_received", label: "Payment Received", desc: "When a payment is recorded" },
  { value: "invoice_created", label: "Invoice Created", desc: "When an invoice is generated" },
  { value: "task_completed", label: "Task Completed", desc: "When a task is marked done" },
  { value: "lead_created", label: "Lead Created", desc: "When a new lead is added" },
  { value: "appointment_set", label: "Appointment Set", desc: "When an appointment is scheduled" },
  { value: "appointment_completed", label: "Appointment Completed", desc: "When an appointment is marked completed" },
  { value: "appointment_cancelled", label: "Appointment Cancelled", desc: "When an appointment is cancelled" },
];

const STEP_TYPES = [
  { value: "send_email", label: "Send Email to Customer", icon: Mail, color: "bg-blue-500" },
  { value: "send_internal_email", label: "Send Internal Email", icon: MailPlus, color: "bg-indigo-500" },
  { value: "create_task", label: "Create Task", icon: CheckSquare, color: "bg-amber-500" },
  { value: "change_stage", label: "Move Stage", icon: ArrowRightCircle, color: "bg-purple-500" },
  { value: "delay", label: "Wait / Delay", icon: Clock, color: "bg-slate-500" },
  { value: "condition", label: "Check Condition", icon: ShieldCheck, color: "bg-emerald-500" },
];

const CONDITION_TYPES = [
  { value: "still_in_stage", label: "Project is still in trigger stage" },
  { value: "invoice_unpaid", label: "Has unpaid invoices" },
  { value: "no_activity", label: "No recent activity on project" },
];

const STOP_CONDITIONS = [
  { value: "stage_changed", label: "Project leaves the trigger stage", desc: "Stops the workflow when the project moves to any other stage" },
  { value: "project_sold", label: "Project is marked sold/approved", desc: "Stops when project reaches an approved stage" },
  { value: "project_closed", label: "Project is closed or rejected", desc: "Stops when project is rejected or closed" },
  { value: "payment_complete", label: "Payment is complete", desc: "Stops when all invoices are paid" },
];

const newStep = (type = "send_email") => ({
  id: crypto.randomUUID(),
  step_type: type,
  delay_value: type === "delay" ? 1 : null,
  delay_unit: type === "delay" ? "days" : null,
  condition_type: type === "condition" ? "still_in_stage" : null,
  condition_fallback: "stop",
  email_template_id: null,
  subject: "",
  body: "",
  email_style: "human",
  recipient_type: type === "send_internal_email" ? "rep" : "customer",
  task_title: "",
  task_description: "",
  task_assignee: "rep",
  target_stage: null,
  label: "",
  enabled: true,
});

export default function WorkflowBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === "new";
  const initialType = searchParams.get("type") || "stage";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState({});

  const [form, setForm] = useState({
    name: "",
    description: "",
    workflow_type: initialType,
    trigger_type: initialType === "event" ? "event" : "stage_enter",
    trigger_stage: "",
    trigger_event: "",
    service_scope: "all",
    steps: [],
    stop_conditions: ["stage_changed", "project_sold"],
    enabled: true,
  });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [stgRes, catRes, etRes] = await Promise.all([
        axios.get(`${API}/pipeline-stages`, { headers }),
        axios.get(`${API}/pipeline-categories`, { headers }),
        axios.get(`${API}/email-templates`, { headers }),
      ]);
      setStages(stgRes.data);
      setCategories(catRes.data);
      setEmailTemplates(etRes.data);

      if (!isNew) {
        const wfRes = await axios.get(`${API}/workflows/${id}`, { headers });
        setForm({
          name: wfRes.data.name || "",
          description: wfRes.data.description || "",
          workflow_type: wfRes.data.workflow_type || "stage",
          trigger_type: wfRes.data.trigger_type || "stage_enter",
          trigger_stage: wfRes.data.trigger_stage || "",
          trigger_event: wfRes.data.trigger_event || "",
          service_scope: wfRes.data.service_scope || "all",
          steps: wfRes.data.steps || [],
          stop_conditions: wfRes.data.stop_conditions || [],
          enabled: wfRes.data.enabled ?? true,
        });
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load workflow data");
    }
    setLoading(false);
  }, [id, isNew]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter stages by service scope
  const filteredStages = stages.filter(s => {
    if (form.service_scope === "all") return true;
    return s.category === form.service_scope;
  });

  const stagesByCategory = categories.map(cat => ({
    ...cat,
    stages: filteredStages.filter(s => s.category === cat.value),
  })).filter(cat => cat.stages.length > 0);

  const getStageName = (val) => stages.find(s => s.value === val)?.name || val?.replace(/_/g, " ") || "";

  // Step management
  const addStep = (type) => {
    const s = newStep(type);
    setForm(prev => ({ ...prev, steps: [...prev.steps, s] }));
    setExpandedSteps(prev => ({ ...prev, [s.id]: true }));
  };

  const updateStep = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const removeStep = (idx) => {
    setForm(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== idx) }));
  };

  const moveStep = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= form.steps.length) return;
    setForm(prev => {
      const steps = [...prev.steps];
      [steps[idx], steps[newIdx]] = [steps[newIdx], steps[idx]];
      return { ...prev, steps };
    });
  };

  const toggleExpand = (stepId) => {
    setExpandedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const toggleStopCondition = (condition) => {
    setForm(prev => ({
      ...prev,
      stop_conditions: prev.stop_conditions.includes(condition)
        ? prev.stop_conditions.filter(c => c !== condition)
        : [...prev.stop_conditions, condition],
    }));
  };

  // Save workflow
  const handleSave = async () => {
    if (!form.name) { toast.error("Workflow name is required"); return; }
    if (form.workflow_type === "stage" && !form.trigger_stage) { toast.error("Select a trigger stage"); return; }
    if (form.workflow_type === "event" && !form.trigger_event) { toast.error("Select a trigger event"); return; }
    if (form.steps.length === 0) { toast.error("Add at least one step"); return; }

    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API}/workflows`, form, { headers });
        toast.success("Workflow created!");
        navigate(`/automations/build/${res.data.id}`, { replace: true });
      } else {
        await axios.put(`${API}/workflows/${id}`, form, { headers });
        toast.success("Workflow saved!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  // Generate plain-language summary
  const generateSummary = () => {
    const parts = [];
    const scope = SERVICE_SCOPES.find(s => s.value === form.service_scope);

    if (form.workflow_type === "event") {
      const evt = EVENT_TRIGGERS.find(e => e.value === form.trigger_event);
      const evtLabel = evt?.label?.toLowerCase() || form.trigger_event;
      if (form.service_scope !== "all") {
        parts.push(`When ${evtLabel} on a ${scope?.label?.toLowerCase()} project`);
      } else {
        parts.push(`When ${evtLabel}`);
      }
    } else {
      const triggerLabel = getStageName(form.trigger_stage);
      if (form.service_scope !== "all") {
        parts.push(`When a ${scope?.label?.toLowerCase()} project enters "${triggerLabel}"`);
      } else {
        parts.push(`When a project enters "${triggerLabel}"`);
      }
    }

    for (const s of form.steps) {
      if (s.step_type === "delay") parts.push(`wait ${s.delay_value || 1} ${s.delay_unit || "days"}`);
      else if (s.step_type === "condition") {
        const cl = CONDITION_TYPES.find(c => c.value === s.condition_type);
        parts.push(cl ? cl.label.toLowerCase() : "check condition");
      }
      else if (s.step_type === "send_email") parts.push(`send email to ${s.recipient_type}`);
      else if (s.step_type === "send_internal_email") parts.push(`send internal email to ${s.recipient_type}`);
      else if (s.step_type === "create_task") parts.push(`create task "${s.task_title || "Follow up"}"`);
      else if (s.step_type === "change_stage") parts.push(`move to "${getStageName(s.target_stage)}"`);
    }

    const stopLabels = { stage_changed: "stage changes", project_sold: "sold", project_closed: "closed", payment_complete: "paid" };
    if (form.stop_conditions.length > 0) {
      parts.push(`Stops if ${form.stop_conditions.map(s => stopLabels[s] || s).join(" or ")}`);
    }

    return parts.join(", then ").replace(", then Stops", ". Stops") + ".";
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="workflow-builder">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="flex items-center justify-between px-4 sm:px-6 h-14">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/automations")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div className="h-5 w-px bg-slate-200" />
            <Zap className="w-5 h-5 text-[#0F4C81]" />
            <Input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Workflow name..."
              className="border-0 text-base font-semibold h-9 focus-visible:ring-0 focus-visible:ring-offset-0 w-64"
              data-testid="workflow-name"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Active</Label>
              <Switch checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} data-testid="workflow-active-toggle" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" data-testid="save-workflow-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6">
        {/* Summary */}
        {form.trigger_stage && form.steps.length > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-5" data-testid="workflow-summary">
            <div className="flex items-start gap-2">
              <Eye className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800 leading-relaxed">{generateSummary()}</p>
            </div>
          </div>
        )}

        {/* Configuration */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#0F4C81]" />Trigger & Configuration
          </h3>

          {/* Workflow Type Toggle */}
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Workflow Type</Label>
            <div className="flex rounded-lg overflow-hidden border border-slate-300 w-fit">
              <button
                type="button"
                onClick={() => setForm({ ...form, workflow_type: "stage", trigger_type: "stage_enter", trigger_event: "" })}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${form.workflow_type === "stage" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                data-testid="wf-type-stage"
              >
                Stage Workflow
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, workflow_type: "event", trigger_type: "event", trigger_stage: "" })}
                className={`px-4 py-2 text-sm font-semibold transition-colors border-l border-slate-300 ${form.workflow_type === "event" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                data-testid="wf-type-event"
              >
                Event Workflow
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Service Scope</Label>
              <Select value={form.service_scope} onValueChange={v => setForm({ ...form, service_scope: v, trigger_stage: "" })}>
                <SelectTrigger className="h-9" data-testid="service-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_SCOPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.workflow_type === "stage" ? (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Trigger Stage *</Label>
                <Select value={form.trigger_stage || "none"} onValueChange={v => setForm({ ...form, trigger_stage: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="trigger-stage"><SelectValue placeholder="Select stage..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select a stage...</SelectItem>
                    {stagesByCategory.map(cat => (
                      <div key={cat.value}>
                        <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wide">{cat.name}</div>
                        {cat.stages.map(s => (
                          <SelectItem key={s.value} value={s.value}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color || "#6B7280" }} />
                              {s.name}
                            </div>
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Trigger Event *</Label>
                <Select value={form.trigger_event || "none"} onValueChange={v => setForm({ ...form, trigger_event: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="trigger-event"><SelectValue placeholder="Select event..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select an event...</SelectItem>
                    {EVENT_TRIGGERS.map(e => (
                      <SelectItem key={e.value} value={e.value}>
                        <div className="flex flex-col">
                          <span>{e.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Description</Label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What does this workflow do?" className="h-9" data-testid="workflow-desc" />
          </div>

          {/* Stop Conditions */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-500 font-semibold">Stop Conditions</Label>
            <div className="grid grid-cols-2 gap-2">
              {STOP_CONDITIONS.map(sc => (
                <button
                  key={sc.value}
                  type="button"
                  onClick={() => toggleStopCondition(sc.value)}
                  className={`flex items-start gap-2 p-2.5 rounded-lg border text-left transition-all ${
                    form.stop_conditions.includes(sc.value)
                      ? "border-red-300 bg-red-50/50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`stop-${sc.value}`}
                >
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    form.stop_conditions.includes(sc.value) ? "border-red-500 bg-red-500" : "border-slate-300"
                  }`}>
                    {form.stop_conditions.includes(sc.value) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-700">{sc.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{sc.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* STEPS FLOW */}
        <div className="space-y-0">
          {/* Trigger indicator */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#0F4C81] flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Trigger</p>
              <p className="text-sm font-medium text-slate-700">
                {form.workflow_type === "event"
                  ? (form.trigger_event ? `When ${(EVENT_TRIGGERS.find(e => e.value === form.trigger_event)?.label || form.trigger_event).toLowerCase()}` : "Select a trigger event above")
                  : (form.trigger_stage ? `When project enters "${getStageName(form.trigger_stage)}"` : "Select a trigger stage above")
                }
              </p>
            </div>
          </div>

          {/* Connector */}
          {form.steps.length > 0 && <div className="ml-5 h-4 border-l-2 border-dashed border-slate-300" />}

          {/* Steps */}
          {form.steps.map((s, idx) => {
            const stepInfo = STEP_TYPES.find(t => t.value === s.step_type) || STEP_TYPES[0];
            const Icon = stepInfo.icon;
            const isExpanded = expandedSteps[s.id];

            return (
              <div key={s.id} data-testid={`step-${idx}`}>
                {/* Step Card */}
                <div className={`border rounded-xl overflow-hidden transition-all ${isExpanded ? "border-slate-300 shadow-sm" : "border-slate-200"} ${s.enabled === false ? "opacity-60" : ""}`}>
                  {/* Step Header */}
                  <div
                    className="flex items-center gap-3 px-4 py-2.5 bg-white cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleExpand(s.id)}
                  >
                    <div className={`w-8 h-8 rounded-lg ${s.enabled === false ? "bg-slate-300" : stepInfo.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700">
                        {s.label || stepInfo.label}
                        {s.enabled === false && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">off</span>}
                      </p>
                      {s.step_type === "delay" && <p className="text-xs text-slate-400">{s.delay_value} {s.delay_unit}</p>}
                      {s.step_type === "send_email" && s.subject && <p className="text-xs text-slate-400 truncate">{s.subject}</p>}
                      {s.step_type === "send_internal_email" && <p className="text-xs text-slate-400">To: {s.recipient_type}</p>}
                      {s.step_type === "condition" && <p className="text-xs text-slate-400">{CONDITION_TYPES.find(c => c.value === s.condition_type)?.label}</p>}
                      {s.step_type === "create_task" && s.task_title && <p className="text-xs text-slate-400 truncate">{s.task_title}</p>}
                      {s.step_type === "change_stage" && <p className="text-xs text-slate-400">{getStageName(s.target_stage)}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Per-step on/off. Lets you silence ONE step (e.g. a
                          duplicate internal alert) without disabling the whole
                          workflow and losing its customer-facing sends. */}
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                        <Switch
                          checked={s.enabled !== false}
                          onCheckedChange={(v) => updateStep(idx, "enabled", v)}
                          data-testid={`step-enabled-toggle-${idx}`}
                          title={s.enabled === false ? "This step is skipped" : "This step runs"}
                        />
                      </div>
                      <Badge className="text-[10px] bg-slate-100 text-slate-500">{idx + 1}</Badge>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {/* Step Detail */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-3">
                      {/* Step Type Selector */}
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Step Type</Label>
                        <div className="grid grid-cols-3 gap-1.5">
                          {STEP_TYPES.map(t => {
                            const TIcon = t.icon;
                            return (
                              <button key={t.value} type="button" onClick={() => updateStep(idx, "step_type", t.value)}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-all ${
                                  s.step_type === t.value ? "border-[#0F4C81] bg-[#0F4C81]/5 text-[#0F4C81]" : "border-slate-200 text-slate-500 hover:border-slate-300"
                                }`}
                                data-testid={`step-${idx}-type-${t.value}`}
                              >
                                <TIcon className="w-3.5 h-3.5" />{t.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Label */}
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Step Label</Label>
                        <Input value={s.label || ""} onChange={e => updateStep(idx, "label", e.target.value)} placeholder="e.g., Day 2 Check-In" className="h-8 text-sm" data-testid={`step-${idx}-label`} />
                      </div>

                      {/* DELAY CONFIG */}
                      {s.step_type === "delay" && (
                        <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200">
                          <Label className="text-sm text-slate-600">Wait</Label>
                          <Input type="number" min="1" value={s.delay_value || 1} onChange={e => updateStep(idx, "delay_value", parseInt(e.target.value) || 1)} className="w-20 h-8 text-sm text-center" data-testid={`step-${idx}-delay-val`} />
                          <Select value={s.delay_unit || "days"} onValueChange={v => updateStep(idx, "delay_unit", v)}>
                            <SelectTrigger className="w-24 h-8 text-sm" data-testid={`step-${idx}-delay-unit`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="minutes">Minutes</SelectItem>
                              <SelectItem value="hours">Hours</SelectItem>
                              <SelectItem value="days">Days</SelectItem>
                              <SelectItem value="weeks">Weeks</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* CONDITION CONFIG */}
                      {s.step_type === "condition" && (
                        <div className="p-3 bg-white rounded-lg border border-emerald-200 space-y-2">
                          <Label className="text-xs text-slate-500">Check if...</Label>
                          <Select value={s.condition_type || "still_in_stage"} onValueChange={v => updateStep(idx, "condition_type", v)}>
                            <SelectTrigger className="h-8 text-sm" data-testid={`step-${idx}-cond-type`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CONDITION_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-slate-500">If NOT met:</Label>
                            <Select value={s.condition_fallback || "stop"} onValueChange={v => updateStep(idx, "condition_fallback", v)}>
                              <SelectTrigger className="w-40 h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="stop">Stop workflow</SelectItem>
                                <SelectItem value="skip">Skip & continue</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      {/* EMAIL CONFIG */}
                      {(s.step_type === "send_email" || s.step_type === "send_internal_email") && (
                        <div className="p-3 bg-white rounded-lg border border-blue-200 space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Recipient</Label>
                              <Select value={s.recipient_type || "customer"} onValueChange={v => updateStep(idx, "recipient_type", v)}>
                                <SelectTrigger className="h-8 text-sm" data-testid={`step-${idx}-recipient`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="customer">Customer</SelectItem>
                                  <SelectItem value="rep">Assigned Rep</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Style</Label>
                              <Select value={s.email_style || "system"} onValueChange={v => updateStep(idx, "email_style", v)}>
                                <SelectTrigger className="h-8 text-sm" data-testid={`step-${idx}-style`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="system">System (Clean & Direct)</SelectItem>
                                  <SelectItem value="human">Personal (Warm)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {/* Template selector */}
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Use Email Template (optional)</Label>
                            <Select value={s.email_template_id || "none"} onValueChange={v => {
                              const tpl = emailTemplates.find(t => t.id === v);
                              if (tpl) {
                                updateStep(idx, "email_template_id", v);
                                updateStep(idx, "subject", tpl.subject);
                                updateStep(idx, "body", tpl.body);
                                updateStep(idx, "email_style", tpl.style);
                              } else {
                                updateStep(idx, "email_template_id", null);
                              }
                            }}>
                              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select template..." /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None (custom email)</SelectItem>
                                {emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Subject Line</Label>
                            <Input value={s.subject || ""} onChange={e => updateStep(idx, "subject", e.target.value)} placeholder="e.g., Just checking in, {{first_name}}" className="h-8 text-sm" data-testid={`step-${idx}-subject`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Email Body</Label>
                            <Textarea value={s.body || ""} onChange={e => updateStep(idx, "body", e.target.value)} placeholder="Write your email..." rows={5} className="text-sm" data-testid={`step-${idx}-body`} />
                            <p className="text-[10px] text-slate-400">Variables: {"{{first_name}}"}, {"{{contact_name}}"}, {"{{project_address}}"}, {"{{company_name}}"}, {"{{rep_name}}"}</p>
                          </div>
                        </div>
                      )}

                      {/* TASK CONFIG */}
                      {s.step_type === "create_task" && (
                        <div className="p-3 bg-white rounded-lg border border-amber-200 space-y-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Task Title</Label>
                            <Input value={s.task_title || ""} onChange={e => updateStep(idx, "task_title", e.target.value)} placeholder="e.g., Follow up call with customer" className="h-8 text-sm" data-testid={`step-${idx}-task-title`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Description</Label>
                            <Textarea value={s.task_description || ""} onChange={e => updateStep(idx, "task_description", e.target.value)} placeholder="Task details..." rows={2} className="text-sm" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Assign To</Label>
                            <Select value={s.task_assignee || "rep"} onValueChange={v => updateStep(idx, "task_assignee", v)}>
                              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="rep">Assigned Rep</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      {/* STAGE CHANGE CONFIG */}
                      {s.step_type === "change_stage" && (
                        <div className="p-3 bg-white rounded-lg border border-purple-200 space-y-2">
                          <Label className="text-xs text-slate-500">Move project to stage</Label>
                          <Select value={s.target_stage || "none"} onValueChange={v => updateStep(idx, "target_stage", v === "none" ? null : v)}>
                            <SelectTrigger className="h-8 text-sm" data-testid={`step-${idx}-target-stage`}><SelectValue placeholder="Select stage..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" disabled>Select...</SelectItem>
                              {stagesByCategory.map(cat => (
                                <div key={cat.value}>
                                  <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase">{cat.name}</div>
                                  {cat.stages.map(st => <SelectItem key={st.value} value={st.value}>{st.name}</SelectItem>)}
                                </div>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Step Actions */}
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-slate-400" onClick={() => moveStep(idx, -1)} disabled={idx === 0}>
                            <ChevronUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-slate-400" onClick={() => moveStep(idx, 1)} disabled={idx === form.steps.length - 1}>
                            <ChevronDown className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => removeStep(idx)} data-testid={`step-${idx}-delete`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" />Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Connector line */}
                {idx < form.steps.length - 1 && <div className="ml-5 h-3 border-l-2 border-dashed border-slate-300" />}
              </div>
            );
          })}
        </div>

        {/* Add Step Buttons */}
        <div className="mt-4 pt-4 border-t border-dashed border-slate-300">
          <p className="text-xs text-slate-400 mb-2 font-medium">Add a step:</p>
          <div className="flex flex-wrap gap-2">
            {STEP_TYPES.map(t => {
              const TIcon = t.icon;
              return (
                <Button key={t.value} type="button" variant="outline" size="sm" onClick={() => addStep(t.value)}
                  className="h-8 text-xs gap-1.5" data-testid={`add-step-${t.value}`}>
                  <TIcon className="w-3.5 h-3.5" />{t.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
